import { nonPrimaryLocationFromFirestoreDocument } from "dashboard/src/models/locations";
import {
  Timestamp,
  Unsubscribe,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type {
  FirestoreLocationDocument,
  NonPrimaryLocation,
} from "@dashboard/types";

export interface LocationApi {
  // Emits the restaurant's non primary locations, and again on every change.
  // The primary location is derived from the restaurant, not stored here.
  subscribeToLocations(
    restaurantUuid: string,
    onChange: (locations: NonPrimaryLocation[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe;

  // Updates an existing location.
  updateLocation(nonPrimaryLocation: NonPrimaryLocation): Promise<void>;

  // Creates a new location.
  newLocation(location: NonPrimaryLocation): Promise<void>;

  deleteLocation(restaurantUuid: string, locationId: string): Promise<void>;
}

/**
 * An implementation of the LocationApi that uses Firestore as the backend.
 */
export class FirestoreBackedLocationApi implements LocationApi {
  private readonly firestoreRefs: FirestoreRefs;

  constructor(firestoreRefs: FirestoreRefs) {
    this.firestoreRefs = firestoreRefs;
  }

  public subscribeToLocations(
    restaurantUuid: string,
    onChange: (locations: NonPrimaryLocation[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      query(
        this.firestoreRefs.locations(restaurantUuid),
        orderBy("createdAt", "asc")
      ),
      (snapshot) =>
        onChange(
          snapshot.docs
            .map((doc) => doc.data() as FirestoreLocationDocument)
            // we will only show locations that are operational
            // the ability to close a business is not yet implemented.
            .filter((location) => location.businessStatus === "operational")
            .map(nonPrimaryLocationFromFirestoreDocument)
        ),
      onError
    );
  }

  updateLocation(nonPrimaryLocation: NonPrimaryLocation): Promise<void> {
    const { isPrimary, ...updates } = nonPrimaryLocation;
    return setDoc(
      this.firestoreRefs.location(
        nonPrimaryLocation.restaurantUuid,
        nonPrimaryLocation.id
      ),
      updates,
      {
        merge: true,
      }
    );
  }

  newLocation(location: NonPrimaryLocation): Promise<void> {
    const { isPrimary, ...locationWithoutIsPrimary } = location;
    const docData = {
      ...locationWithoutIsPrimary,

      businessStatus: "operational", // newly created locations are always operational

      createdAt: Timestamp.now(),
    } as FirestoreLocationDocument;

    return setDoc(
      this.firestoreRefs.location(location.restaurantUuid, location.id),
      docData,
      { merge: true }
    );
  }

  // Currently implemented as setting the business status to closed_permanently.
  public deleteLocation(
    restaurantUuid: string,
    locationId: string
  ): Promise<void> {
    // Primary locations do not exist as a separate location document in Firestore —
    // they are stored as the restaurant document itself. Attempting to soft-delete
    // with restaurantUuid as the locationId would create an orphaned stub document
    // at restaurants-v1/{uuid}/locations/{uuid} via the merge operation below.
    if (locationId === restaurantUuid) {
      throw new Error("Cannot delete a primary location");
    }

    return setDoc(
      this.firestoreRefs.location(restaurantUuid, locationId),
      {
        businessStatus: "closed_permanently",
      },
      { merge: true }
    );
  }
}
